import { MapPin, Pencil, Check, X } from "lucide-react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex justify-between text-xs py-1 first:pt-0 last:pb-0 border-b border-border/30 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

interface AuctionPickupCardProps {
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
  onUpdate?: (fields: { pickupAddress?: string; pickupPostalCode?: string; pickupCity?: string }) => Promise<void>;
}

export function AuctionPickupCard({
  address,
  postalCode,
  city,
  onUpdate,
}: AuctionPickupCardProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleSave = async (key: string) => {
    if (!onUpdate) return;
    const fieldMap: Record<string, string> = {
      "Adresse": "pickupAddress",
      "Postnummer": "pickupPostalCode",
      "By": "pickupCity",
    };
    await onUpdate({ [fieldMap[key]]: editValue });
    setEditingKey(null);
  };

  const rows = [
    { label: "Adresse", value: address },
    { label: "Postnummer", value: postalCode },
    { label: "By", value: city },
  ];

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          Hentested
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex justify-between items-center text-xs py-1 first:pt-0 last:pb-0 border-b border-border/30 last:border-0 group min-h-[32px]">
            <span className="text-muted-foreground">{label}</span>
            
            {editingKey === label ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                  className="bg-white border border-border rounded px-1.5 py-0.5 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-accent w-32"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave(label);
                    if (e.key === "Escape") setEditingKey(null);
                  }}
                />
                <button onClick={() => handleSave(label)} className="text-green-600 hover:bg-muted p-1 rounded">
                  <Check className="h-3 w-3" />
                </button>
                <button onClick={() => setEditingKey(null)} className="text-muted-foreground hover:bg-muted p-1 rounded">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-foreground font-medium">{value || "Ikke satt"}</span>
                {onUpdate && (
                  <button
                    onClick={() => {
                      setEditingKey(label);
                      setEditValue(value || "");
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-muted transition-all text-muted-foreground"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
